import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageCircle, Users, Flag, Trash2, Ban, Check, X, 
  Loader2, AlertTriangle, Eye, Shield, ChevronDown, ChevronUp,
  Send, RefreshCw, Clock, User, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ChatAdmin() {
  const [rooms, setRooms] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoom, setExpandedRoom] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [activeTab, setActiveTab] = useState('rooms'); // 'rooms' or 'reports'
  const [roomMessages, setRoomMessages] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(null);
  
  // Create Room Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRoom, setNewRoom] = useState({
    name: '',
    description: '',
    rules: '',
    room_type: 'community',
    is_private: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [roomsRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/chat/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/chat/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRooms(roomsRes.data.rooms);
      setReports(reportsRes.data.reports);
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoomMessages = async (roomId) => {
    setLoadingMessages(roomId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/chat/rooms/${roomId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoomMessages(prev => ({
        ...prev,
        [roomId]: response.data.messages
      }));
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(null);
    }
  };

  const handleExpandRoom = async (roomId) => {
    if (expandedRoom === roomId) {
      setExpandedRoom(null);
    } else {
      setExpandedRoom(roomId);
      // Load messages if not already loaded
      if (!roomMessages[roomId]) {
        await loadRoomMessages(roomId);
      }
    }
  };

  const refreshRoomMessages = async (roomId) => {
    await loadRoomMessages(roomId);
    toast.success('Messages refreshed');
  };

  const terminateRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to terminate this room? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/chat/rooms/${roomId}/terminate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Room terminated');
      loadData();
    } catch (error) {
      toast.error('Failed to terminate room');
    }
  };

  const resolveReport = async (reportId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/chat/reports/${reportId}/resolve?action=${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Report ${action}`);
      loadData();
    } catch (error) {
      toast.error('Failed to resolve report');
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Permanently delete this report? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/chat/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/chat/messages/${messageId}/delete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const createRoom = async () => {
    if (!newRoom.name || !newRoom.rules) {
      toast.error('Name and rules are required');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/chat/rooms`, newRoom, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chat room created!');
      setShowCreateModal(false);
      setNewRoom({ name: '', description: '', rules: '', room_type: 'community', is_private: false });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  const pendingReports = reports.filter(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold">Chat Management</h2>
            <p className="text-xs md:text-sm text-neutral-400">Monitor rooms & reports</p>
          </div>
        </div>
        {pendingReports.length > 0 && (
          <span className="px-2 md:px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-xs md:text-sm flex items-center gap-1 md:gap-2 self-start md:self-auto">
            <AlertTriangle className="w-3 h-3 md:w-4 md:h-4" />
            {pendingReports.length} pending
          </span>
        )}
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-white text-black hover:bg-neutral-200"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Room
        </Button>
      </div>

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-2xl p-6 max-w-md w-full border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Create Chat Room</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Room Name *</label>
                <Input
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({...newRoom, name: e.target.value})}
                  placeholder="e.g., AI Product Discussions"
                  className="bg-neutral-800 border-white/10"
                />
              </div>
              
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Description</label>
                <Input
                  value={newRoom.description}
                  onChange={(e) => setNewRoom({...newRoom, description: e.target.value})}
                  placeholder="What's this room about?"
                  className="bg-neutral-800 border-white/10"
                />
              </div>
              
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Room Rules *</label>
                <textarea
                  value={newRoom.rules}
                  onChange={(e) => setNewRoom({...newRoom, rules: e.target.value})}
                  placeholder="1. Be respectful&#10;2. No spam&#10;3. Stay on topic"
                  className="w-full p-3 bg-neutral-800 border border-white/10 rounded-lg text-white resize-none text-sm"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-sm text-neutral-400 mb-1 block">Room Type</label>
                <select
                  value={newRoom.room_type}
                  onChange={(e) => setNewRoom({...newRoom, room_type: e.target.value})}
                  className="w-full p-3 bg-neutral-800 border border-white/10 rounded-lg text-white text-sm"
                >
                  <option value="community">Community (Anyone can join)</option>
                  <option value="creator_lounge">Creator Lounge (Creators only)</option>
                  <option value="deals">Deals & Discounts</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_private"
                  checked={newRoom.is_private}
                  onChange={(e) => setNewRoom({...newRoom, is_private: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="is_private" className="text-sm text-neutral-400">Private room (invite only)</label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={createRoom}
                  disabled={creating}
                  className="bg-white text-black hover:bg-neutral-200"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Room
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 md:mb-6">
        <Button
          variant={activeTab === 'rooms' ? 'default' : 'outline'}
          onClick={() => setActiveTab('rooms')}
          size="sm"
          className={activeTab === 'rooms' ? 'bg-[#00F0FF] text-black' : 'border-white/20'}
        >
          <Users className="w-4 h-4 mr-1 md:mr-2" />
          <span className="text-sm">Rooms ({rooms.length})</span>
        </Button>
        <Button
          variant={activeTab === 'reports' ? 'default' : 'outline'}
          onClick={() => setActiveTab('reports')}
          size="sm"
          className={activeTab === 'reports' ? 'bg-red-500' : 'border-white/20'}
        >
          <Flag className="w-4 h-4 mr-1 md:mr-2" />
          <span className="text-sm">Reports ({pendingReports.length})</span>
        </Button>
      </div>

      {/* Rooms Tab */}
      {activeTab === 'rooms' && (
        <div className="space-y-3">
          {rooms.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">No chat rooms yet</p>
          ) : (
            rooms.map((room) => (
              <div 
                key={room.room_id} 
                className={`border rounded-xl overflow-hidden ${
                  room.is_active ? 'border-white/10 bg-neutral-800/30' : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <div 
                  className="p-3 md:p-4 flex flex-col md:flex-row md:items-center justify-between gap-2 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => handleExpandRoom(room.room_id)}
                >
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${room.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold flex flex-wrap items-center gap-1 md:gap-2 text-sm md:text-base">
                        <span className="truncate">{room.name}</span>
                        {room.is_system_room && (
                          <span className="text-xs px-1.5 md:px-2 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF] whitespace-nowrap">System</span>
                        )}
                        {!room.is_active && (
                          <span className="text-xs px-1.5 md:px-2 py-0.5 rounded bg-red-500/20 text-red-400 whitespace-nowrap">Ended</span>
                        )}
                      </h3>
                      <p className="text-xs text-neutral-400">
                        {room.member_count || 0} members
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end md:self-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandRoom(room.room_id);
                      }}
                      className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10 px-2 md:px-3"
                    >
                      <Eye className="w-4 h-4 md:mr-1" />
                      <span className="hidden md:inline text-sm">View</span>
                    </Button>
                    {room.is_active && !room.is_system_room && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          terminateRoom(room.room_id);
                        }}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 px-2"
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                    {expandedRoom === room.room_id ? (
                      <ChevronUp className="w-4 h-4 text-neutral-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-neutral-400" />
                    )}
                  </div>
                </div>
                
                {expandedRoom === room.room_id && (
                  <div className="border-t border-white/5 bg-neutral-900/50">
                    {/* Room Info */}
                    <div className="p-4 border-b border-white/5">
                      <p className="text-sm text-neutral-400 mb-2">{room.description}</p>
                      <div className="text-xs text-neutral-500 grid grid-cols-2 gap-2">
                        <p><strong>Type:</strong> {room.room_type}</p>
                        <p><strong>Creator:</strong> {room.creator_name || room.creator_id}</p>
                      </div>
                    </div>
                    
                    {/* Messages Section */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-[#00F0FF]" />
                          Chat Activity
                        </h4>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            refreshRoomMessages(room.room_id);
                          }}
                          disabled={loadingMessages === room.room_id}
                          className="text-neutral-400 hover:text-white"
                        >
                          <RefreshCw className={`w-4 h-4 ${loadingMessages === room.room_id ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      
                      {loadingMessages === room.room_id ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-5 h-5 animate-spin text-[#00F0FF]" />
                        </div>
                      ) : roomMessages[room.room_id]?.length > 0 ? (
                        <div className="max-h-80 overflow-y-auto space-y-2 bg-black/30 rounded-lg p-3">
                          {roomMessages[room.room_id].map((msg) => (
                            <div 
                              key={msg.message_id} 
                              className={`p-3 rounded-lg ${
                                msg.is_deleted ? 'bg-red-500/10 border border-red-500/20' : 'bg-neutral-800/50'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <User className="w-3 h-3 text-neutral-500" />
                                  <span className="font-medium text-sm">{msg.user_name || 'Unknown'}</span>
                                  {msg.user_role === 'creator' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF]">Creator</span>
                                  )}
                                  {msg.user_role === 'admin' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF]">Admin</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-neutral-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(msg.created_at).toLocaleString()}
                                  </span>
                                  {!msg.is_deleted && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteMessage(msg.message_id);
                                      }}
                                      className="h-6 w-6 p-0 text-neutral-500 hover:text-red-400 hover:bg-red-500/10"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <p className={`text-sm ${msg.is_deleted ? 'text-red-400 italic' : 'text-neutral-200'}`}>
                                {msg.is_deleted ? '[Message deleted]' : msg.content}
                              </p>
                              {msg.product_id && (
                                <div className="mt-1 text-xs text-[#00F0FF] bg-[#00F0FF]/10 inline-block px-2 py-0.5 rounded">
                                  📦 Shared product
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-neutral-500">
                          <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No messages in this room yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-3">
          {reports.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">No reports yet</p>
          ) : (
            reports.map((report) => (
              <div 
                key={report.report_id} 
                className={`border rounded-xl overflow-hidden ${
                  report.status === 'pending' 
                    ? 'border-yellow-500/30 bg-yellow-500/5' 
                    : report.status === 'action_taken'
                    ? 'border-green-500/20 bg-neutral-800/30'
                    : 'border-white/10 bg-neutral-800/30'
                }`}
              >
                {/* Clickable Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setExpandedReport(expandedReport === report.report_id ? null : report.report_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        report.status === 'action_taken' ? 'bg-green-500/20 text-green-400' :
                        'bg-neutral-500/20 text-neutral-400'
                      }`}>
                        {report.status === 'action_taken' ? 'action taken' : report.status}
                      </span>
                      <span className="font-medium">{report.reason}</span>
                      <span className="text-xs text-neutral-500">
                        {new Date(report.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {report.status === 'pending' && (
                        <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
                      )}
                      {expandedReport === report.report_id ? (
                        <ChevronUp className="w-4 h-4 text-neutral-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-neutral-400" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">
                    Room: {report.room_name} • Reported by: {report.reporter_name}
                  </p>
                </div>

                {/* Expanded Details */}
                {expandedReport === report.report_id && (
                  <div className="p-4 border-t border-white/5 bg-neutral-900/50">
                    {/* Reported Message */}
                    {report.message_content && (
                      <div className="mb-4">
                        <p className="text-xs text-neutral-500 mb-2 font-semibold">REPORTED MESSAGE</p>
                        <div className="p-3 bg-neutral-800/80 rounded-lg border border-white/5">
                          <p className="text-xs text-neutral-400 mb-1">
                            By: {report.message_author} {report.message_author_email && `(${report.message_author_email})`}
                          </p>
                          <p className="text-neutral-200">"{report.message_content}"</p>
                        </div>
                      </div>
                    )}

                    {/* Report Details */}
                    {report.details && (
                      <div className="mb-4">
                        <p className="text-xs text-neutral-500 mb-2 font-semibold">REPORTER'S COMMENTS</p>
                        <p className="text-sm text-neutral-300 bg-neutral-800/50 p-3 rounded-lg">{report.details}</p>
                      </div>
                    )}

                    {/* Resolution Info */}
                    {report.status !== 'pending' && report.resolved_at && (
                      <div className="mb-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20 flex items-center justify-between">
                        <p className="text-xs text-green-400">
                          ✓ Resolved on {new Date(report.resolved_at).toLocaleString()}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReport(report.report_id);
                          }}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete Report
                        </Button>
                      </div>
                    )}

                    {/* Actions (only for pending) */}
                    {report.status === 'pending' && (
                      <div className="flex gap-2 pt-2 border-t border-white/5">
                        {report.message_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMessage(report.message_id);
                            }}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Message
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            resolveReport(report.report_id, 'action_taken');
                          }}
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Action Taken
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            resolveReport(report.report_id, 'dismissed');
                          }}
                          className="border-neutral-500/30 text-neutral-400 hover:bg-neutral-500/10"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default ChatAdmin;
